import { studentStatus, teacherStatus, TOPICS_TYPES } from '@helpers/constants';
import {
  SELECT_ALL,
  CLEAR_SELECTION,
  CURRENTLY_ASSIGNED,
  ACCURACY0TO50,
  ACCURACY51TO84,
  ACCURACY85TO100,
  COMPLETED1TO20,
  COMPLETED21TO40,
  COMPLETED41TO60,
  COMPLETED61TO80,
  COMPLETED81TO100
} from '@components/quick-selector/quick-selector-constants';

import Schema from '@containers/entities/schema';

const { UNASSIGNED, UNATTEMPTED, ASSIGN_SELECTION, UNASSIGN_SELECTION } = studentStatus;
const { REVIEWED, SUBMITTED } = teacherStatus;

export const getStudents = classrooms => {
  if (classrooms.length > 0) {
    const classroom = classrooms[0];
    const group = classroom.groups[0];
    return group.students;
  }
  return [];
};

export const updateStudentSelectedIds = (studentAssignments, id, selectedStudentIds) => {
  let isAllSelected = true;
  // if all of the unattempted and unassigned cells for a row are selected
  // student checkbox should be checked else unchecked
  const currentStudent = studentAssignments.filter(s => s.studentUserProfileId === id);

  currentStudent.forEach(student => {
    if (
      (student.studentStatus === UNATTEMPTED || student.studentStatus === UNASSIGNED) &&
      student.teacherStatus !== REVIEWED &&
      student.teacherStatus !== SUBMITTED
    ) {
      isAllSelected = false;
    }
  });

  if (isAllSelected) {
    selectedStudentIds = [...new Set([...selectedStudentIds, id])];
  } else {
    const index = selectedStudentIds.indexOf(id);
    if (index !== -1) {
      selectedStudentIds.splice(index, 1);
    }
  }
  return selectedStudentIds;
};

export const checkUnsavedChanges = students => {
  let assignSelection = 0;
  let unassignSelection = 0;

  students.forEach(student => {
    switch (student.studentStatus) {
      case ASSIGN_SELECTION:
        assignSelection += 1;
        break;

      case UNASSIGN_SELECTION:
        unassignSelection += 1;
        break;

      default:
        break;
    }
  });

  if (assignSelection > 0 || unassignSelection > 0) {
    return true;
  }

  return false;
};

export const getTeacherNotes = (playLists, playListId, girlId) => {
  const playList = playLists.find(pl => pl.id === playListId);
  const teacherNotes = playList.psrs.find(pl => pl.girlId === girlId);
  teacherNotes.girlId = girlId;
  return teacherNotes;
};

export const getTopicsAndStandards = genericCourses => {
  if (genericCourses.length === 0) return [];

  const topicTree = [];
  const standardTopic = genericCourses.topics.filter(topic => topic.type.toLowerCase() === 'standard');
  const strandTopic = genericCourses.topics.filter(topic => topic.type.toLowerCase() === 'strand');

  if (standardTopic.length > 0) {
    standardTopic.forEach(st => {
      st.displayName = `${st.externalKey.toUpperCase()} ${st.name}`.trim();
      st.selected = true;
    });
    topicTree.push({
      id: -1,
      displayName: 'All Standards',
      childTopics: standardTopic,
      selected: true
    });
  }

  strandTopic.forEach(strand => {
    strand.displayName = strand.name;
    strand.selected = true;
    strand.childTopics.forEach(st => {
      st.displayName = `${st.externalKey.toUpperCase()} ${st.name}`.trim();
      st.selected = true;
    });
    strand.isExpand = !!strand.childTopics.find(s => s.selected);
  });

  if (standardTopic.length === 0 && strandTopic.length > 0) {
    strandTopic[0].selected = true;
    strandTopic[0].childTopics.forEach(ct => {
      ct.selected = true;
    });
    strandTopic[0].isExpand = !!strandTopic[0].childTopics.find(s => s.selected);
  }

  topicTree.push(...strandTopic);
  return topicTree;
};

const getMatchingStrands = (strands, searchTerm) => {
  const matchingStrands = [];
  strands.forEach(topic => {
    const matchedStandards = topic.childTopics.filter(ct =>
      ct.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (matchedStandards.length) {
      topic.childTopics = matchedStandards;
      matchingStrands.push(topic);
    }
  });

  return matchingStrands;
};

export const getFilteredStrandAndStandards = (searchTerm, topics) => {
  const matchingStandards = getMatchingStrands(topics, searchTerm);
  if (matchingStandards.length > 0) {
    const matchingTopicId = matchingStandards.map(t => t.id);
    const unMatchedTopics = topics.filter(t => matchingTopicId.indexOf(t.id) === -1);
    const matchingTopics = unMatchedTopics.filter(t => t.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
    return [...matchingStandards, ...matchingTopics];
  }
  return topics.filter(t => t.displayName.toLowerCase().includes(searchTerm.toLowerCase()));
};

export const updateStrandAndStandards = (id, checked, topics) => {
  if (id) {
    if (id.indexOf('#') !== -1) {
      const topic = topics.find(t => t.id.toString() === id.split('#')[0]);
      const childTopic = topic.childTopics.find(t => t.id.toString() === id.split('#')[1]);
      childTopic.selected = checked;
      topic.selected = !!topic.childTopics.find(s => s.selected);
      topic.isExpand = topic.selected;
    } else {
      const topic = topics.find(t => t.id.toString() === id);
      topic.selected = checked;
      topic.childTopics.forEach(ct => {
        ct.selected = checked;
      });
      topic.isExpand = !!topic.childTopics.find(s => s.selected);
    }
  }
  return topics;
};

export const updateStrands = (checked, topics) => {
  topics.forEach(topic => {
    topic.selected = checked;
    topic.childTopics.forEach(ct => {
      ct.selected = checked;
    });
    topic.isExpand = !!topic.childTopics.find(s => s.selected);
  });

  return topics;
};

export const removeStandard = (id, topics) =>
  topics.map(topic => ({
    ...topic,
    childTopics: topic.childTopics.map(ct => ({ ...ct, selected: ct.id === id ? false : ct.selected }))
  }));

export const updateCount = (id, topics = [], studentAssignments = []) => {
  let selectedTopic = [];
  let LJIds = [];
  topics.forEach(topic => {
    topic.childTopics.forEach(ct => {
      if (ct.id === id) {
        selectedTopic = ct;
      }
    });
  });

  LJIds = selectedTopic && selectedTopic.childTopics && selectedTopic.childTopics.map(ct => ct.id);
  return studentAssignments.filter(assignment => !LJIds.includes(assignment.learningJourneyId));
};

export const updateAssignmentStatus = (leaningJourneyId, studentId, assignmentStatuses) => {
  const filteredAssignmentStatus = assignmentStatuses.find(
    a => a.studentProfileId === studentId && a.learningJourneyId === leaningJourneyId
  );
  filteredAssignmentStatus.studentStatus =
    filteredAssignmentStatus.studentStatus === ASSIGN_SELECTION ? UNASSIGNED : ASSIGN_SELECTION;
  return assignmentStatuses;
};

export const getAllLearningJourneys = selectedTopics => {
  const learningJourneyIds = [];
  selectedTopics.forEach(strands => {
    strands.childTopics.forEach(standards => {
      if (standards.selected) {
        standards.childTopics.forEach(learningJourney => {
          learningJourneyIds.push(learningJourney.id);
        });
      }
    });
  });
  return learningJourneyIds;
};

export const updateGridCells = (
  isLeaningJourney,
  studentId,
  leaningJourneyId,
  assignmentStatuses,
  studentAssignments,
  status,
  clonedSelectedLJIDs,
  clonedStudentIds,
  learningJourneyIds,
  studentUnAssignments
) => {
  let filteredArray = [];
  let filteredStudentAssignments = [];
  if (isLeaningJourney) {
    filteredArray = assignmentStatuses.filter(a => a.learningJourneyId === leaningJourneyId);
  } else {
    filteredArray = assignmentStatuses.filter(a => a.studentProfileId === studentId);
  }
  filteredArray.forEach(val => {
    if (
      (val.studentStatus === UNASSIGNED || val.studentStatus === UNATTEMPTED) &&
      status === 'checked' &&
      learningJourneyIds.includes(val.learningJourneyId)
    ) {
      if (status === 'checked') {
        if (val.studentStatus === UNATTEMPTED && (val.stepsCount === 0 || val.stepsCompleted === 0)) {
          studentUnAssignments.push({
            studentUserProfileId: val.studentProfileId,
            learningJourneyId: val.learningJourneyId,
            assignmentId: val.assignmentId
          });
        }
        if (val.studentStatus === UNASSIGNED) {
          studentAssignments.push({
            studentUserProfileId: val.studentProfileId,
            learningJourneyId: val.learningJourneyId
          });
        }
      }
      val.studentStatus = val.studentStatus === UNATTEMPTED ? UNASSIGN_SELECTION : ASSIGN_SELECTION;
    } else if (
      (val.studentStatus === ASSIGN_SELECTION || val.studentStatus === UNASSIGN_SELECTION) &&
      status === 'unchecked'
    ) {
      val.studentStatus = val.studentStatus === UNASSIGN_SELECTION ? UNATTEMPTED : UNASSIGNED;
    }
  });

  if (status !== 'checked') {
    if (studentAssignments.length > 0) {
      if (isLeaningJourney) {
        filteredStudentAssignments = studentAssignments.filter(a => a.learningJourneyId !== leaningJourneyId);
      } else {
        filteredStudentAssignments = studentAssignments.filter(a => a.studentUserProfileId !== studentId);
      }
      studentAssignments = filteredStudentAssignments;
    }
    if (studentUnAssignments.length > 0) {
      if (isLeaningJourney) {
        filteredStudentAssignments = studentUnAssignments.filter(a => a.learningJourneyId !== leaningJourneyId);
      } else {
        filteredStudentAssignments = studentUnAssignments.filter(a => a.studentUserProfileId !== studentId);
      }
      studentUnAssignments = filteredStudentAssignments;
    }

    if (isLeaningJourney) {
      clonedSelectedLJIDs.splice(clonedSelectedLJIDs.indexOf(leaningJourneyId), 1);
    } else {
      clonedStudentIds.splice(clonedStudentIds.indexOf(studentId), 1);
    }
  } else if (isLeaningJourney) {
    clonedSelectedLJIDs.push(leaningJourneyId);
  } else {
    clonedStudentIds.push(studentId);
  }

  return { assignmentStatuses, studentAssignments, clonedSelectedLJIDs, clonedStudentIds, studentUnAssignments };
};

export const updateUnAssignmentStatus = (leaningJourneyId, studentId, assignmentStatuses, status) => {
  const filteredAssignmentStatus = assignmentStatuses.find(
    a => a.studentProfileId === studentId && a.learningJourneyId === leaningJourneyId
  );
  filteredAssignmentStatus.studentStatus = status ? UNASSIGN_SELECTION : UNATTEMPTED;
  return assignmentStatuses;
};

export const filterStandardByLJCount = (selectedTopics = [], LJCount = 0) => {
  if (selectedTopics.length === 0) return [];
  let count = 0;
  let standardwithLJCount = 0;

  selectedTopics.forEach(topic => {
    if (topic && topic.childTopics) {
      topic.childTopics.forEach(childTopic => {
        count += (childTopic && childTopic.childTopics && childTopic.childTopics.length) || 0;
        if (childTopic.childTopics.length) {
          standardwithLJCount += 1;
        }
        if (standardwithLJCount > 1) {
          childTopic.selected = !(count > LJCount);
          if (!childTopic.selected) topic.selected = false;
        }
      });
    }
  });
  return selectedTopics;
};

export const getSelectedStandardCount = courses => {
  let selectedStandardCount = 0;
  let standardCount = 0;

  courses.forEach(val => {
    val.childTopics.forEach(st => {
      standardCount += 1;
      if (st.selected) {
        selectedStandardCount += 1;
      }
    });
  });

  return { selectedStandardCount, standardCount };
};

export function sortStudentAssignmentsById(studentAssignments, sortedStudents) {
  const orderedArr = [];
  sortedStudents.forEach(sorted => {
    studentAssignments.forEach(currentStudent => {
      if (currentStudent.studentProfileId === sorted.id) {
        orderedArr.push({
          ...currentStudent,
          metaData: {
            ...currentStudent?.metaData,
            student: sorted
          }
        });
      }
    });
  });
  return orderedArr;
}

export function updateAssignmentsHidden(studentAssignments, learningJourneys) {
  const orderedArr = studentAssignments.map(currentStudent => {
    if (!learningJourneys.includes(currentStudent.learningJourneyId)) {
      return {
        ...currentStudent,
        metaData: {
          ...currentStudent.metaData,
          isHidden: true
        }
      };
    }

    return {
      ...currentStudent,
      metaData: {
        ...currentStudent.metaData,
        isHidden: false
      }
    };
  });
  return orderedArr;
}

export const checkAccuracyCellSelectable = (selectionType, learningJourneyResult) => {
  const { stepsCompleted, accuracy } = learningJourneyResult || {};

  const hasCompletedSteps = stepsCompleted > 0;

  const selectableType = {
    [SELECT_ALL]: true,
    [CLEAR_SELECTION]: false,
    [CURRENTLY_ASSIGNED]: stepsCompleted >= 0,
    [ACCURACY0TO50]: accuracy >= 0 && accuracy < 51 && hasCompletedSteps,
    [ACCURACY51TO84]: accuracy >= 51 && accuracy < 85 && hasCompletedSteps,
    [ACCURACY85TO100]: accuracy >= 85 && accuracy <= 100 && hasCompletedSteps
  };
  return selectableType[selectionType] || false;
};

export const checkProgressCellSelectable = (selectionType, learningJourneyResult, stepsCount) => {
  const stepsCompleted = learningJourneyResult?.stepsCompleted;

  // Don't need to calculate percentage when total steps are 0
  const completedPercentage = stepsCount > 0 ? (stepsCompleted / stepsCount) * 100 : 0;
  const hasCompletedSteps = stepsCompleted > 0;

  const selectableType = {
    [SELECT_ALL]: true,
    [CLEAR_SELECTION]: false,
    [CURRENTLY_ASSIGNED]: stepsCompleted >= 0,
    [COMPLETED1TO20]: completedPercentage >= 1 && completedPercentage < 21 && hasCompletedSteps,
    [COMPLETED21TO40]: completedPercentage >= 21 && completedPercentage < 41 && hasCompletedSteps,
    [COMPLETED41TO60]: completedPercentage >= 41 && completedPercentage < 61 && hasCompletedSteps,
    [COMPLETED61TO80]: completedPercentage >= 61 && completedPercentage < 81 && hasCompletedSteps,
    [COMPLETED81TO100]: completedPercentage >= 81 && completedPercentage <= 100 && hasCompletedSteps
  };
  return selectableType[selectionType] || false;
};

export const findSchemaByTopics = (topics = []) => {
  const firstTopTopic = topics[0] || {};

  if (firstTopTopic.type === TOPICS_TYPES.STANDARD) return Schema.STANDARDS_ARRAY;

  return Schema.STRANDS_ARRAY;
};
